/**
 * HTTP Client
 * Configured axios instance with auth and tenant interceptors
 * Now with automatic token refresh on 401 errors
 */

import axios from 'axios';
import authService from '../auth/authService';
import { getStoredTokens } from '../auth/tokenManager';
import { logSecurityEvent, SecurityEventType } from '../auth/securityMonitor';

/**
 * Create configured axios instance
 */
const createHttpClient = () => {
  const client = axios.create({
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // Request interceptor: Add auth token
  client.interceptors.request.use(
    async (config) => {
      try {
        // Get tokens from encrypted storage
        const storedTokens = await getStoredTokens();
        if (storedTokens?.idToken) {
          config.headers.Authorization = `Bearer ${storedTokens.idToken}`;
        }
      } catch (error) {
        // No authenticated user, continue without auth header
        console.warn('No authenticated user for API request:', error.message);
      }

      // NOTE: X-Tenant-ID header removed - it's redundant and causes CORS errors
      // Backend Lambda functions extract tenant context from JWT token via the authorizer
      // The authorizer decodes the JWT and passes tenantId in event.requestContext.authorizer

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle errors with token refresh retry
  client.interceptors.response.use(
    (response) => {
      return response.data;
    },
    async (error) => {
      const originalRequest = error.config;

      // Handle 401 Unauthorized - attempt token refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        // Skip token refresh for auth endpoints (login, signup, etc.)
        const isAuthEndpoint = originalRequest.url?.includes('/auth/');
        if (isAuthEndpoint) {
          // For auth endpoints, just reject - these are login failures, not expired tokens
          return Promise.reject(error);
        }

        // Check if user has tokens to refresh
        const storedTokens = await getStoredTokens();
        if (!storedTokens?.refreshToken) {
          // No refresh token = user not logged in, don't attempt refresh
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
          console.log('401 error detected, attempting token refresh...');

          // Attempt to refresh tokens
          await authService.refreshTokens();

          // Retry original request with new token
          const refreshedTokens = await getStoredTokens();
          if (refreshedTokens?.idToken) {
            originalRequest.headers.Authorization = `Bearer ${refreshedTokens.idToken}`;
            console.log('Retrying request with refreshed token');
            return client.request(originalRequest);
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          // Refresh failed, sign out user
          console.warn('Signing out due to failed token refresh');
          await authService.signOut();
          // Reload page to trigger login redirect
          window.location.reload();
          return Promise.reject(refreshError);
        }
      }

      // For other errors or already retried, reject
      if (error.response?.status === 401) {
        console.warn('Unauthorized request after retry, signing out');

        // Log security event
        logSecurityEvent(SecurityEventType.UNAUTHORIZED_API_CALL, {
          url: originalRequest.url,
          method: originalRequest.method
        });

        await authService.signOut();
        // Reload page to trigger login redirect
        window.location.reload();
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// Export singleton instance
export default createHttpClient();
